import { useEffect } from "react";

/**
 * Imperatively injects a JSON-LD <script type="application/ld+json"> tag into
 * <head>. Each instance is keyed by `id` so pages can safely mount multiple
 * schemas (e.g., Organization + WebSite + Article) without collisions.
 *
 * On unmount the tag is removed, keeping the head clean during SPA navigation.
 *
 * AEO / LLM visibility: ChatGPT (OAI-SearchBot), Perplexity, Claude, Google's
 * AI Overviews all preferentially cite pages carrying accurate schema.org
 * structured data. This is the single highest-leverage AEO lever.
 */
export default function StructuredData({ id, data }) {
  const dataKey = JSON.stringify(data);
  useEffect(() => {
    if (!id || !data) return undefined;
    const tagId = `ld-${id}`;
    let el = document.getElementById(tagId);
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = tagId;
      document.head.appendChild(el);
    }
    try {
      el.textContent = dataKey;
    } catch {
      /* ignore */
    }
    return () => {
      const node = document.getElementById(tagId);
      if (node && node.parentNode) node.parentNode.removeChild(node);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, dataKey]);

  return null;
}

/* -------- Prebuilt payload builders (kept here so pages stay tidy) -------- */

const SITE = "https://artistryperspectives.com";

export const organizationLD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE}/#organization`,
  name: "Artistry Perspectives",
  alternateName: "Artistry Perspectives Studio",
  url: SITE,
  logo: `${SITE}/logo-spade.png`,
  image: `${SITE}/og-image.jpg`,
  description:
    "A creative studio for ambitious brands — cinematic brand films, strategy, editorial, and digital experiences.",
  sameAs: [
    "https://www.instagram.com/artistryperspectives",
  ],
  contactPoint: [
    {
      "@type": "ContactPoint",
      contactType: "customer service",
      email: "hello@artistryperspectives.com",
      areaServed: "Worldwide",
      availableLanguage: ["English"],
    },
  ],
};

export const websiteLD = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE}/#website`,
  url: SITE,
  name: "Artistry Perspectives",
  publisher: { "@id": `${SITE}/#organization` },
  potentialAction: {
    "@type": "SearchAction",
    target: `${SITE}/blog?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
};

export function creativeWorkLD(work) {
  const url = `${SITE}/work/${work.slug}`;
  return {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    "@id": `${url}#creativework`,
    name: work.title,
    headline: work.title,
    description: work.subtitle || work.description || undefined,
    url,
    image: work.cover_image || undefined,
    dateCreated: work.year ? String(work.year) : undefined,
    creator: { "@id": `${SITE}/#organization` },
    publisher: { "@id": `${SITE}/#organization` },
    about: work.category || undefined,
    keywords: [work.category, work.services].filter(Boolean).join(", ") || undefined,
  };
}

export function blogPostingLD(post) {
  const url = `${SITE}/blog/${post.slug}`;
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "@id": `${url}#blogposting`,
    headline: post.title,
    description: post.excerpt || post.seo_description || undefined,
    image: post.cover_image || undefined,
    url,
    mainEntityOfPage: url,
    datePublished: post.published_at || undefined,
    dateModified: post.updated_at || post.published_at || undefined,
    author: { "@id": `${SITE}/#organization` },
    publisher: { "@id": `${SITE}/#organization` },
    keywords: Array.isArray(post.tags) ? post.tags.join(", ") : undefined,
    wordCount: post.read_time_min ? post.read_time_min * 200 : undefined,
  };
}

export function faqPageLD(items = []) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: it.a,
      },
    })),
  };
}

export function breadcrumbLD(crumbs = []) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: c.item.startsWith("http") ? c.item : `${SITE}${c.item}`,
    })),
  };
}
